import React, { useState, useRef } from "react";
import { styled } from "styled-components";
import { Subtitle } from "./shared/Subtitle";
import { Title } from "./shared/Title";
import {
  FormControlLabel,
  TextField,
  Checkbox,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { PrimaryButton } from "./shared/Button";
import Grid from "@mui/material/Grid";
import { theme } from "../theme";
import emailjs from "@emailjs/browser";
import { Link } from "react-router-dom";
import { countries } from "./shared/countries";
import { CTAText } from "./shared/CTAText";

const Root = styled.div(
  ({ theme }) => `
background-image: url('img/teacher-background.png');
background-size: cover;
background-position: center;
background-repeat: no-repeat;
padding-bottom: 50px;
  `
);

const WrapperStyled = styled.div`
  margin: 70px auto 0;
  max-width: 1000px;
  padding: 50px 5%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  & .description {
    max-width: 750px;
    margin: 20px auto;
    text-align: left;
  }

  & .centeredText {
    max-width: 600px;
    margin: 20px auto 40px auto;
    text-align: center;
  }
`;

const FormStyled = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Links = styled.span(
  ({ theme }) => `
  & a {
    color: ${theme.colors.blue}
  }  
  `
);

const textFieldStyles = {
  borderRadius: "5px",
  backgroundColor: "#EFEFEF",
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#EFEFEF",
    },
    "&:hover fieldset": {
      borderColor: theme.colors.blue,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.colors.blue,
    },
  },
  "& .MuiInputLabel-root": {
    color: "gray",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.colors.blue,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
};

const ContainerButton = styled.div`
  margin-top: 25px;
  width: 100%;
`;

export const Contact = () => {
  const form = useRef();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [checked, setChecked] = useState(false);
  const [formState, setFormState] = useState({
    submitting: false,
    success: false,
    error: false,
  });

  // Nuevo estado para errores de validación
  const [errors, setErrors] = useState({});

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  // Nueva función de validación
  const validate = () => {
    const newErrors = {};
    if (!firstName)
      newErrors.firstName = "¿Cómo te llaman cuando te regañan? Eso va aquí.";
    if (!lastName) newErrors.lastName = "Porfa, somos curiosos.";
    if (!country) newErrors.country = "¿Planeta Tierra, verdad?";
    if (!phoneNumber)
      newErrors.phoneNumber = "Solo para darte buenas noticias.";
    if (!email) newErrors.email = "No usamos lechuzas, necesitamos tu correo.";
    if (!checked)
      newErrors.checked =
        "Solo falta aceptar esto y tu viaje puede comenzar 🚀";
    return newErrors;
  };

  const sendEmail = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return; // No envía si hay errores
    }

    setFormState((prevState) => ({ ...prevState, submitting: true }));

    const emailOptions = {
      email,
      first_name: firstName,
      phone: phoneNumber,
      country
    };
    emailjs
      .send("service_584kxr8", "template_y856nto", emailOptions, {
        publicKey: "ATnw2CyneX_JxCF6q",
      })
      .then(
        () => {
          setFormState((prevState) => ({
            ...prevState,
            submitting: false,
            success: true,
          }));
        },
        (error) => {
          setFormState((prevState) => ({
            ...prevState,
            submitting: false,
            error: true,
          }));
        }
      );
  };

  // Nueva función para reiniciar el formulario tras error
  const handleRetry = () => {
    setFormState({
      submitting: false,
      success: false,
      error: false,
    });
    setErrors({});
  };

  return (
    <Root>
      <WrapperStyled id="contact">
        <Subtitle>Tu futuro como desarrollador está a un paso</Subtitle>
        <Title>¿Listo para cambiar tu vida? Habla con nosotros</Title>
        <p className="description">
          Si estás decidido a dar el salto y convertirte en desarrollador web,
          estamos aquí para ayudarte.
          <br />
          <br />
          Resuelve tus dudas, conoce todos los detalles del programa y descubre
          cómo podemos guiarte hacia el éxito.
          <br />
          <br />
          Completa el formulario y <b>te contactaremos en menos de 24 horas.</b>
        </p>
        <CTAText>
          ¡Tu nueva vida profesional está más cerca de lo que imaginas!
        </CTAText>
        {formState.submitting && <CircularProgress />}
        {formState.success ? (
          <div style={{ marginTop: 40, textAlign: "center" }}>
            <p style={{ color: theme.blue, fontSize: 20 }}>
              ✅ ¡Gracias futuro alumno! Revisa tu correo, estamos preparando
              todo para ti.
            </p>
          </div>
        ) : formState.error ? (
          <div style={{ marginTop: 40, textAlign: "center" }}>
            <p style={{ color: "red", fontSize: 18 }}>
              Houston, tenemos un problema... 👩‍🚀
            </p>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleRetry();
              }}
              style={{
                color: theme.colors.blue,
                textDecoration: "underline",
                cursor: "pointer",
                fontWeight: 500,
                display: "inline-block",
                marginTop: 16,
              }}
            >
              Reintentar
            </a>
          </div>
        ) : (
          <FormStyled>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  label="Nombre"
                  variant="outlined"
                  sx={textFieldStyles}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  label="Apellido"
                  variant="outlined"
                  sx={textFieldStyles}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!errors.country}
                >
                  <InputLabel id="country-code-label">País</InputLabel>
                  <Select
                    fullWidth
                    labelId="country-code-label"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    required
                    sx={{
                      ...textFieldStyles,
                      "& .MuiSelect-select": {
                        textAlign: "left",
                      },
                    }}
                    style={{
                      height: !!errors.country ? 78 : "auto",
                    }}
                    label="País"
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.country} ({country.code})
                      </MenuItem>
                    ))}
                  </Select>
                  {!!errors.country && (
                    <FormHelperText
                      style={{
                        position: "relative",
                        top: "-22px",
                        marginBottom: "-22px",
                      }}
                    >
                      {errors.country}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                  label="Teléfono"
                  variant="outlined"
                  sx={textFieldStyles}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  label="Correo"
                  variant="outlined"
                  sx={textFieldStyles}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </Grid>
            </Grid>
            <FormControlLabel
              style={{ marginTop: 30 }}
              control={
                <Checkbox
                  checked={checked}
                  onChange={handleChange}
                  sx={{
                    color: theme.colors.blue,
                    "&.Mui-checked": {
                      color: theme.colors.blue,
                    },
                  }}
                />
              }
              label={
                <Links>
                  Acepto la{" "}
                  <Link to="/privacy-policy" target="_blank">
                    Política de Privacidad
                  </Link>{" "}
                  y los{" "}
                  <Link to="/terms-and-conditions" target="_blank">
                    Términos de Uso
                  </Link>
                  .
                  {errors.checked && (
                    <div
                      style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}
                    >
                      {errors.checked}
                    </div>
                  )}
                </Links>
              }
              slotProps={{
                typography: { sx: { marginLeft: "10px", textAlign: "left" } },
              }}
            />
            <ContainerButton>
              <PrimaryButton $large onClick={sendEmail}>
                Enviar
              </PrimaryButton>
            </ContainerButton>
          </FormStyled>
        )}
      </WrapperStyled>
    </Root>
  );
};
